import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import axios from '../../utils/axios';
import { useSnackbar } from '../snackbar';

import './ModalAvaliacaoAcolhimento.css';

// ----------------------------------------------------------------------

const ASSUNTOS = [
    'Ansiedade',
    'Luto',
    'Relacionamento',
    'Solidao',
    'Autoestima',
    'Trabalho / carreira',
    'Outro',
];

const ASSUNTOS_LABEL: { [key: string]: string } = {
    Solidao: 'Solidão',
};

const OPCOES_INICIO = [
    { value: 'muito_abalado', label: 'Muito abalado' },
    { value: 'abalado', label: 'Abalado' },
    { value: 'estavel', label: 'Estável' },
];

const OPCOES_FIM = [
    { value: 'mais_calmo', label: 'Mais calmo(a)' },
    { value: 'sem_mudanca', label: 'Sem mudança' },
    { value: 'mais_abalado', label: 'Mais abalado(a)' },
];

const OPCOES_AJUDOU = [
    { value: 'sim', label: 'Sim' },
    { value: 'nao', label: 'Não' },
    { value: 'nao_sei', label: 'Não tenho certeza' },
];

const OPCOES_SIM_NAO = [
    { value: 'sim', label: 'Sim' },
    { value: 'nao', label: 'Não' },
];

const OPCOES_COMO_ESTA = [
    { value: 'bem', label: 'Bem' },
    { value: 'cansado', label: 'Cansado(a)' },
    { value: 'afetado', label: 'Emocionalmente afetado(a)' },
    { value: 'preciso_apoio', label: 'Preciso de apoio' },
];

type Opcao = { value: string; label: string };

type props = {
    open: any;
    setOpen: any;
    tokenConversa?: any;
    IDAtendimento?: any;
};

export default function ModalAvaliacaoAcolhimento({ open, setOpen, tokenConversa, IDAtendimento }: props) {
    const { enqueueSnackbar } = useSnackbar();

    const [assunto, setAssunto] = useState<string>('');
    const [heroiInicio, setHeroiInicio] = useState<string>('');
    const [heroiFim, setHeroiFim] = useState<string>('');
    const [conseguiuAjudar, setConseguiuAjudar] = useState<string>('');
    const [teveDuvida, setTeveDuvida] = useState<string>('');
    const [duvidaTexto, setDuvidaTexto] = useState<string>('');
    const [riscoSuicidio, setRiscoSuicidio] = useState<string>('');
    const [precisaAcompanhamento, setPrecisaAcompanhamento] = useState<string>('');
    const [comoEsta, setComoEsta] = useState<string>('');
    const [querFalarEquipe, setQuerFalarEquipe] = useState<string>('');
    const [comentario, setComentario] = useState<string>('');
    const [enviando, setEnviando] = useState<boolean>(false);

    // se o anjo já avaliou esse atendimento, o modal abre com as respostas anteriores
    const carregar = useCallback(async () => {
        try {
            const response = await axios.post(`/areadoatendente/avaliacao-acolhimento/get`, {
                tokenConversa,
                IDAtendimento,
            });

            const { avaliacao } = response.data;

            if (!avaliacao) {
                return;
            }

            setAssunto(avaliacao.assunto || '');
            setHeroiInicio(avaliacao.heroi_inicio || '');
            setHeroiFim(avaliacao.heroi_fim || '');
            setConseguiuAjudar(avaliacao.conseguiu_ajudar || '');
            setTeveDuvida(avaliacao.teve_duvida || '');
            setDuvidaTexto(avaliacao.duvida_texto || '');
            setRiscoSuicidio(avaliacao.risco_suicidio || '');
            setPrecisaAcompanhamento(avaliacao.precisa_acompanhamento || '');
            setComoEsta(avaliacao.como_esta || '');
            setQuerFalarEquipe(avaliacao.quer_falar_equipe || '');
            setComentario(avaliacao.comentario || '');
        } catch (error) {
            console.error(error);
        }
    }, [tokenConversa, IDAtendimento]);

    useEffect(() => {
        if (open) {
            carregar();
        }
    }, [open, carregar]);

    const handleClose = () => {
        setOpen(false);
    };

    const enviar = async () => {
        setEnviando(true);

        try {
            const response = await axios.post(`/areadoatendente/avaliacao-acolhimento`, {
                tokenConversa,
                IDAtendimento,
                assunto,
                heroi_inicio: heroiInicio,
                heroi_fim: heroiFim,
                conseguiu_ajudar: conseguiuAjudar,
                teve_duvida: teveDuvida,
                duvida_texto: duvidaTexto,
                risco_suicidio: riscoSuicidio,
                precisa_acompanhamento: precisaAcompanhamento,
                como_esta: comoEsta,
                quer_falar_equipe: querFalarEquipe,
                comentario,
                origem: 'web',
            });

            enqueueSnackbar(`${response.data.message}`, { variant: 'success' });
            setEnviando(false);
            handleClose();
        } catch (error) {
            enqueueSnackbar(`${error.message}`, { variant: 'error' });
            setEnviando(false);
        }
    };

    // helper para os grupos de chips
    const grupo = (
        opcoes: Opcao[],
        valor: string,
        setValor: (v: string) => void,
        aria: string,
        cor?: string
    ) => (
        <div className="eyheac-choices" role="radiogroup" aria-label={aria}>
            {opcoes.map((op) => (
                <button
                    key={op.value}
                    type="button"
                    role="radio"
                    aria-checked={valor === op.value}
                    className={`eyheac-choice ${cor || ''} ${valor === op.value ? 'is-checked' : ''}`}
                    onClick={() => setValor(op.value)}
                >
                    {op.label}
                </button>
            ))}
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="eyheacModalTitle"
            className="eyheac-modal-root"
            maxWidth={false}
            PaperProps={{ className: 'eyheac-modal-paper' }}
        >
            <button type="button" className="eyheac-close" aria-label="Fechar" onClick={handleClose}>
                &times;
            </button>

            <div className="eyheac-header">
                <div className="eyheac-header-row">
                    <div className="eyheac-art" aria-hidden="true">
                        <svg width="56" height="56" viewBox="0 0 168 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M20 78c-2-16 8-30 24-32 8-1 14 3 17 9l6 13-14 22c-9 6-20 4-27-3-5-3-6-6-6-9z"
                                fill="#DCE8FF"
                            />
                            <path
                                d="M148 78c2-16-8-30-24-32-8-1-14 3-17 9l-6 13 14 22c9 6 20 4 27-3 5-3 6-6 6-9z"
                                fill="#DCE8FF"
                            />
                            <g className="eyheac-pulse">
                                <path
                                    d="M84 46c-9-13-29-9-29 6 0 15 20 26 29 33 9-7 29-18 29-33 0-15-20-19-29-6z"
                                    fill="#FF8552"
                                />
                            </g>
                            <path
                                d="M32 34l2.4 5.6L40 42l-5.6 2.4L32 50l-2.4-5.6L24 42l5.6-2.4z"
                                fill="#FF8552"
                                opacity="0.55"
                            />
                            <path
                                d="M140 30l1.8 4.2L146 36l-4.2 1.8L140 42l-1.8-4.2L134 36l4.2-1.8z"
                                fill="#2F6FED"
                                opacity="0.5"
                            />
                        </svg>
                    </div>

                    <div className="eyheac-header-content">
                        <span className="eyheac-badge">
                            <span className="eyheac-badge-dot" aria-hidden="true" />
                            Depois da conversa
                        </span>
                        <Typography variant="h6" className="eyheac-title" id="eyheacModalTitle">
                            Como foi esse acolhimento?
                        </Typography>
                        <Typography className="eyheac-subtitle">
                            Leva menos de um minuto. Suas respostas ajudam o EYhe a cuidar melhor de você e do próximo
                            Herói.
                        </Typography>
                    </div>
                </div>
            </div>

            <DialogContent className="eyheac-body">
                {/* Sobre a conversa */}
                <div className="eyheac-section">
                    <Typography component="p" className="eyheac-section-label">
                        <span className="dot" />
                        Sobre a conversa
                    </Typography>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Sobre qual assunto o Herói abordou?
                        </Typography>
                        <select
                            className="eyheac-select"
                            aria-label="Assunto abordado pelo Herói"
                            value={assunto}
                            onChange={(event) => setAssunto(event.target.value)}
                        >
                            <option value="">Selecione um assunto</option>
                            {ASSUNTOS.map((item) => (
                                <option key={item} value={item}>
                                    {ASSUNTOS_LABEL[item] || item}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Como estava o Herói no início da conversa?
                        </Typography>
                        {grupo(OPCOES_INICIO, heroiInicio, setHeroiInicio, 'Como estava o Herói no início da conversa?')}
                    </div>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            E como ele(a) pareceu ao final?
                        </Typography>
                        {grupo(OPCOES_FIM, heroiFim, setHeroiFim, 'Como o Herói pareceu ao final da conversa?')}
                    </div>
                </div>

                <hr className="eyheac-divider" />

                {/* Sua percepção */}
                <div className="eyheac-section">
                    <Typography component="p" className="eyheac-section-label">
                        <span className="dot" />
                        Sua percepção
                    </Typography>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Você acredita que conseguiu ajudar o Herói de alguma forma?
                        </Typography>
                        {grupo(
                            OPCOES_AJUDOU,
                            conseguiuAjudar,
                            setConseguiuAjudar,
                            'Você acredita que conseguiu ajudar o Herói de alguma forma?'
                        )}
                    </div>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Houve algum momento em que você não soube como agir?
                        </Typography>
                        {grupo(
                            OPCOES_SIM_NAO,
                            teveDuvida,
                            setTeveDuvida,
                            'Houve algum momento em que você não soube como agir?'
                        )}

                        {teveDuvida === 'sim' && (
                            <div className="eyheac-reveal">
                                <textarea
                                    className="eyheac-textarea"
                                    aria-label="Conte um pouco sobre esse momento"
                                    placeholder="Conte um pouco sobre esse momento — isso nos ajuda a te apoiar melhor."
                                    value={duvidaTexto}
                                    onChange={(event) => setDuvidaTexto(event.target.value)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <hr className="eyheac-divider" />

                {/* Atenção especial */}
                <div className="eyheac-section risk">
                    <Typography component="p" className="eyheac-section-label">
                        <span className="dot" />
                        Atenção especial
                    </Typography>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            O Herói mencionou pensamentos de se machucar ou de suicídio?
                        </Typography>

                        <div
                            className="eyheac-choices"
                            role="radiogroup"
                            aria-label="O Herói mencionou pensamentos de se machucar ou de suicídio?"
                        >
                            <button
                                type="button"
                                role="radio"
                                aria-checked={riscoSuicidio === 'sim'}
                                className={`eyheac-choice danger ${riscoSuicidio === 'sim' ? 'is-checked' : ''}`}
                                onClick={() => setRiscoSuicidio('sim')}
                            >
                                Sim
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={riscoSuicidio === 'nao'}
                                className={`eyheac-choice ${riscoSuicidio === 'nao' ? 'is-checked' : ''}`}
                                onClick={() => setRiscoSuicidio('nao')}
                            >
                                Não
                            </button>
                        </div>

                        {riscoSuicidio === 'sim' && (
                            <div className="eyheac-callout">
                                <strong>Obrigado por sinalizar.</strong> Nossa equipe será notificada agora para
                                acompanhar esse caso com você. Você não precisa lidar com isso sozinho(a).
                            </div>
                        )}
                    </div>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Você sente que esse caso precisa de acompanhamento da equipe do EYhe?
                        </Typography>
                        {grupo(
                            OPCOES_SIM_NAO,
                            precisaAcompanhamento,
                            setPrecisaAcompanhamento,
                            'Esse caso precisa de acompanhamento da equipe do EYhe?'
                        )}
                    </div>
                </div>

                <hr className="eyheac-divider" />

                {/* E você, como está? */}
                <div className="eyheac-section care">
                    <Typography component="p" className="eyheac-section-label">
                        <span className="dot" />
                        E você, como está?
                    </Typography>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Como você está se sentindo depois dessa conversa?
                        </Typography>
                        <select
                            className="eyheac-select"
                            aria-label="Como você está se sentindo depois dessa conversa?"
                            value={comoEsta}
                            onChange={(event) => setComoEsta(event.target.value)}
                        >
                            <option value="">Selecione</option>
                            {OPCOES_COMO_ESTA.map((op) => (
                                <option key={op.value} value={op.value}>
                                    {op.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label">
                            Gostaria de conversar com alguém da equipe sobre esse atendimento?
                        </Typography>

                        <div
                            className="eyheac-choices"
                            role="radiogroup"
                            aria-label="Gostaria de conversar com alguém da equipe sobre esse atendimento?"
                        >
                            <button
                                type="button"
                                role="radio"
                                aria-checked={querFalarEquipe === 'sim'}
                                className={`eyheac-choice blue ${querFalarEquipe === 'sim' ? 'is-checked' : ''}`}
                                onClick={() => setQuerFalarEquipe('sim')}
                            >
                                Sim
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={querFalarEquipe === 'nao'}
                                className={`eyheac-choice ${querFalarEquipe === 'nao' ? 'is-checked' : ''}`}
                                onClick={() => setQuerFalarEquipe('nao')}
                            >
                                Não
                            </button>
                        </div>
                    </div>
                </div>

                <hr className="eyheac-divider" />

                {/* Comentário livre */}
                <div className="eyheac-section">
                    <div className="eyheac-q">
                        <Typography component="p" className="eyheac-q-label soft">
                            Algum comentário ou sugestão? <span className="eyheac-q-optional">(opcional)</span>
                        </Typography>
                        <textarea
                            className="eyheac-textarea"
                            aria-label="Comentário ou sugestão"
                            placeholder="Fique à vontade para escrever o que quiser."
                            value={comentario}
                            onChange={(event) => setComentario(event.target.value)}
                        />
                    </div>
                </div>
            </DialogContent>

            <DialogActions className="eyheac-footer">
                <button type="button" className="eyheac-skip" onClick={handleClose}>
                    Pular por agora
                </button>
                <button type="button" className="eyheac-submit" onClick={enviar} disabled={enviando}>
                    {enviando ? 'Enviando...' : 'Enviar avaliação'}
                </button>
            </DialogActions>
        </Dialog>
    );
}
